export const content = `
<div class="prose mx-auto py-8">
  <p class="mb-4 text-gray-700 dark:text-gray-300">Dans ce guide actualisé, nous implémentons un <strong class="font-semibold">RAG (Retrieval-Augmented Generation)</strong> 100 % local et souverain en utilisant uniquement <strong class="font-semibold">LangChain</strong> comme framework principal.</p>

  <p class="mb-4 text-gray-700 dark:text-gray-300">Nous utilisons :</p>

  <ul class="list-disc list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300">
    <li><strong class="font-semibold">LangChain</strong> pour tout orchestrer (chargement, découpe, embeddings, vector store, retrieval, prompt, chain)</li>
    <li><strong class="font-semibold">Modèles open-weight Mistral</strong> (via vLLM ou Ollama pour l'inférence locale)</li>
    <li><strong class="font-semibold">Embeddings open-source</strong> (BAAI/bge-m3 via HuggingFaceEmbeddings)</li>
    <li><strong class="font-semibold">Qdrant local</strong> (via langchain-qdrant)</li>
    <li><strong class="font-semibold">PyMuPDFLoader + Tesseract</strong> pour l'OCR/extraction PDF</li>
  </ul>

  <p class="mb-6 text-gray-700 dark:text-gray-300"><strong class="font-semibold">Objectif</strong> : Montrer une intégration IA moderne, performante, économique et souveraine.</p>

  <h2 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-50">Pourquoi LangChain uniquement ?</h2>

  <ul class="list-disc list-inside mb-6 space-y-2 text-gray-700 dark:text-gray-300">
    <li><strong class="font-semibold">Réduit drastiquement le boilerplate</strong> (moins de bugs, plus rapide à prototyper)</li>
    <li><strong class="font-semibold">Composants testés en production</strong> (splitters, retrievers, LCEL)</li>
    <li><strong class="font-semibold">Facile à migrer vers JS/TS</strong> (LangChain.js) pour ton backend Node</li>
    <li><strong class="font-semibold">Écosystème riche</strong> : loaders PDF/OCR, reranking, évaluation, mémoire, agents…</li>
  </ul>

  <h2 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-50">Architecture RAG Hybride & Structurée (via LangChain)</h2>

  <p class="mb-4 text-gray-700 dark:text-gray-300">Voici le schéma en Mermaid (copiez-collez dans un outil comme <a href="https://mermaid.live" class="text-blue-600 hover:underline" target="_blank">Mermaid Live</a> pour le visualiser) :</p>

  <pre class="bg-gray-800 text-white p-4 rounded-lg text-sm overflow-x-auto mb-6"><code>graph TD
    %% Styles
    classDef client fill:#e1f5fe,stroke:#01579b,stroke-width:2px;
    classDef langchain fill:#fff3e0,stroke:#ef6c00,stroke-width:2px,stroke-dasharray: 5 5;
    classDef sovereign fill:#f3e5f5,stroke:#7b1fa2,stroke-width:2px;
    classDef mistral fill:#e8f5e9,stroke:#2e7d32,stroke-width:2px;

    %% 1. ZONE UTILISATEUR
    subgraph Client ["🖥️ Utilisateur"]
        User((Utilisateur))
        Frontend[Frontend Angular / Chat]:::client
    end

    %% 2. ZONE LANGCHAIN (ORCHESTRATION)
    subgraph LangChainOrchestration ["⚙️ LangChain (Orchestration)"]
        PyMuPDF[PyMuPDFLoader + OCR]:::langchain
        Splitter[RecursiveCharacterTextSplitter]:::langchain
        HFEmbed[HuggingFaceEmbeddings&lt;br&gt;(bge-m3)]:::langchain
        QdrantStore[QdrantVectorStore]:::langchain
        Retriever[as_retriever]:::langchain
        LCELChain[LCEL Chain&lt;br&gt;(prompt | llm | parser)]:::langchain
    end

    %% 3. ZONE SOUVERAINE (INFRA LOCALE)
    subgraph SovereignCloud ["🔒 Infra Locale (OVH/Scaleway/Machine)"]
        VectorDB[(Qdrant Docker)]:::sovereign
        vLLM[vLLM / Ollama&lt;br&gt;Mistral-7B / Ministral-8B]:::mistral
    end

    %% FLUX
    User --&gt; Frontend
    Frontend --&gt; PyMuPDF
    PyMuPDF --&gt; Splitter
    Splitter --&gt; HFEmbed
    HFEmbed --&gt; QdrantStore
    QdrantStore --&gt; VectorDB
    Frontend --&gt; Retriever
    Retriever --&gt; QdrantStore
    Retriever --&gt; LCELChain
    LCELChain --&gt; vLLM
    vLLM --&gt; LCELChain
    LCELChain --&gt; Frontend</code></pre>

  <h2 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-50">Étapes d'Implémentation (Code LangChain complet)</h2>

  <p class="mb-4 text-gray-700 dark:text-gray-300">Voici le code Python complet pour implémenter un RAG souverain avec LangChain :</p>

  <pre class="bg-gray-800 text-white p-4 rounded-lg text-sm overflow-x-auto mb-6"><code># requirements.txt (à installer)
# langchain langchain-community langchain-huggingface langchain-qdrant pymupdf pytesseract vllm pillow

from langchain_community.document_loaders import PyMuPDFLoader
from langchain_text_splitters import RecursiveCharacterTextSplitter
from langchain_huggingface import HuggingFaceEmbeddings
from langchain_qdrant import QdrantVectorStore
from langchain_core.prompts import PromptTemplate
from langchain_core.runnables import RunnablePassthrough
from langchain_core.output_parsers import StrOutputParser

# ────────────────────────────────────────────────
# CONFIG
# ────────────────────────────────────────────────
PDF_PATH = "chemin/vers/vos_documents.pdf"
COLLECTION_NAME = "rag_meshbox"
QDRANT_URL = "http://localhost:6333"

EMBED_MODEL = "BAAI/bge-m3"
LLM_MODEL = "mistralai/Mistral-7B-Instruct-v0.2"  # via vLLM

CHUNK_SIZE = 800
CHUNK_OVERLAP = 120
TOP_K = 5

# ────────────────────────────────────────────────
# 1. Embeddings (local)
# ────────────────────────────────────────────────
embeddings = HuggingFaceEmbeddings(model_name=EMBED_MODEL)

# ────────────────────────────────────────────────
# 2. Ingestion complète (une seule fois)
# ────────────────────────────────────────────────
def ingest_documents():
    # Load + OCR si besoin
    loader = PyMuPDFLoader(PDF_PATH, extract_images=True)
    docs = loader.load()

    # Découpe intelligente
    splitter = RecursiveCharacterTextSplitter(
        chunk_size=CHUNK_SIZE,
        chunk_overlap=CHUNK_OVERLAP,
        separators=["\\n\\n", "\\n", ". ", " ", ""]
    )
    chunks = splitter.split_documents(docs)

    # Vector Store LangChain + Qdrant local
    vector_store = QdrantVectorStore.from_documents(
        documents=chunks,
        embedding=embeddings,
        url=QDRANT_URL,
        collection_name=COLLECTION_NAME,
        force_recreate=True   # Pour tests – en prod : False
    )

    print(f"Ingéré {len(chunks)} chunks dans Qdrant via LangChain.")
    return vector_store

# ────────────────────────────────────────────────
# 3. RAG Chain (LCEL – très lisible)
# ────────────────────────────────────────────────
def create_rag_chain(vector_store, llm):
    retriever = vector_store.as_retriever(search_kwargs={"k": TOP_K})

    prompt = PromptTemplate.from_template(
        """Tu es un assistant précis et fiable. Réponds uniquement avec le contexte fourni.
        Si tu n'as pas l'information, dis-le clairement sans inventer.

        Contexte :
        {context}

        Question : {question}

        Réponse concise et structurée :"""
    )

    def format_docs(docs):
        return "\\n\\n".join(doc.page_content for doc in docs)

    # LCEL chain (très puissant)
    chain = (
        {"context": retriever | format_docs, "question": RunnablePassthrough()}
        | prompt
        | llm  # ← ton LLM local (vLLM wrapper ou Ollama)
        | StrOutputParser()
    )

    return chain

# ────────────────────────────────────────────────
# Utilisation
# ────────────────────────────────────────────────
if __name__ == "__main__":
    # 1. Ingérer (exécuter une fois)
    vector_store = ingest_documents()

    # 2. Créer la chain RAG (llm = votre wrapper vLLM ou Ollama)
    # Exemple avec Ollama :
    # from langchain_ollama import OllamaLLM
    # llm = OllamaLLM(model="mistral")
    rag_chain = create_rag_chain(vector_store, llm)

    # 3. Tester interactif
    while True:
        question = input("\\nQuestion (exit pour quitter) : ")
        if question.lower() == "exit":
            break

        answer = rag_chain.invoke(question)
        print("\\nRéponse :\\n", answer)</code></pre>

  <h2 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-50">Avantages de cette version 100 % LangChain</h2>

  <ul class="list-disc list-inside mb-6 space-y-2 text-gray-700 dark:text-gray-300">
    <li><strong class="font-semibold">Moins de code</strong> : ~60 lignes vs 150+ en version custom</li>
    <li><strong class="font-semibold">Standardisation</strong> : Tout est fait avec des composants officiels → facile à maintenir / debugger / scaler</li>
    <li><strong class="font-semibold">Extensible</strong> : Ajoute en 2 lignes :
      <ul class="list-disc list-inside ml-6 mt-2 space-y-1">
        <li>Reranking : <code class="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-red-700 dark:text-red-400">ContextualCompressionRetriever</code></li>
        <li>Mémoire conversation : <code class="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-red-700 dark:text-red-400">ConversationBufferMemory</code></li>
        <li>Évaluation : <code class="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-red-700 dark:text-red-400">RAGAS / TruLens</code></li>
        <li>Agent : <code class="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-red-700 dark:text-red-400">create_react_agent</code></li>
      </ul>
    </li>
    <li><strong class="font-semibold">Migration JS/TS</strong> : Identique en LangChain.js (même LCEL, même classes)</li>
  </ul>

  <h2 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-50">Configuration Docker (Infra Souveraine)</h2>

  <p class="mb-4 text-gray-700 dark:text-gray-300">Exemple <code class="bg-gray-200 dark:bg-gray-700 px-1 py-0.5 rounded text-red-700 dark:text-red-400">docker-compose.yml</code> pour déployer Qdrant et vLLM localement :</p>

  <pre class="bg-gray-800 text-white p-4 rounded-lg text-sm overflow-x-auto mb-6"><code>version: '3.8'
services:
  qdrant:
    image: qdrant/qdrant:latest
    ports:
      - "6333:6333"
    volumes:
      - qdrant_data:/qdrant/storage

  vllm:
    image: vllm/vllm-openai:latest
    command: --model mistralai/Mistral-7B-Instruct-v0.2
    ports:
      - "8000:8000"
    deploy:
      resources:
        limits:
          cpus: '4'
          memory: 16G
    runtime: nvidia  # Si GPU disponible

volumes:
  qdrant_data:</code></pre>

  <h2 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-50">Espace disque (rappel actualisé)</h2>

  <div class="overflow-x-auto mb-6">
    <table class="min-w-full border border-gray-300 dark:border-gray-600">
      <thead class="bg-gray-100 dark:bg-gray-700">
        <tr>
          <th class="px-4 py-2 text-left text-gray-900 dark:text-gray-50 font-semibold border-b border-gray-300 dark:border-gray-600">Élément</th>
          <th class="px-4 py-2 text-left text-gray-900 dark:text-gray-50 font-semibold border-b border-gray-300 dark:border-gray-600">Taille estimée</th>
        </tr>
      </thead>
      <tbody class="text-gray-700 dark:text-gray-300">
        <tr class="border-b border-gray-200 dark:border-gray-600">
          <td class="px-4 py-2">Modèle LLM (Mistral-7B)</td>
          <td class="px-4 py-2">13–15 GB</td>
        </tr>
        <tr class="border-b border-gray-200 dark:border-gray-600">
          <td class="px-4 py-2">Embeddings (bge-m3)</td>
          <td class="px-4 py-2">1.5–2.5 GB</td>
        </tr>
        <tr class="border-b border-gray-200 dark:border-gray-600">
          <td class="px-4 py-2">Qdrant + Docker</td>
          <td class="px-4 py-2">2–5 GB</td>
        </tr>
        <tr class="border-b border-gray-200 dark:border-gray-600">
          <td class="px-4 py-2">LangChain deps + cache</td>
          <td class="px-4 py-2">1–2 GB</td>
        </tr>
        <tr class="border-b border-gray-200 dark:border-gray-600">
          <td class="px-4 py-2">Documents + chunks + vecteurs (100k chunks)</td>
          <td class="px-4 py-2">2–10 GB</td>
        </tr>
        <tr class="bg-gray-50 dark:bg-gray-800 font-semibold">
          <td class="px-4 py-2">Total recommandé</td>
          <td class="px-4 py-2">50–100 Go SSD (confort)</td>
        </tr>
      </tbody>
    </table>
  </div>

  <h2 class="text-3xl font-bold mt-8 mb-4 text-gray-900 dark:text-gray-50">Conclusion</h2>

  <p class="mb-4 text-gray-700 dark:text-gray-300">Cette version LangChain est idéale pour démontrer une architecture RAG souveraine :</p>

  <blockquote class="border-l-4 border-mesh-500 pl-4 italic mb-6 text-gray-700 dark:text-gray-300">
    « On utilise LangChain pour orchestrer l'ensemble du pipeline RAG de façon standardisée, souveraine et locale, avec Qdrant et Mistral open-weight. »
  </blockquote>

  <p class="mb-4 text-gray-700 dark:text-gray-300">Les points clés à retenir :</p>

  <ul class="list-disc list-inside mb-4 space-y-2 text-gray-700 dark:text-gray-300">
    <li>Utilisez <strong class="font-semibold">LangChain</strong> pour réduire le boilerplate et standardiser votre pipeline RAG.</li>
    <li>Déployez <strong class="font-semibold">Qdrant + vLLM/Ollama</strong> sur infrastructure souveraine (OVH/Scaleway) pour la compliance RGPD.</li>
    <li>Utilisez les <strong class="font-semibold">embeddings open-source</strong> (bge-m3) pour éliminer les dépendances aux API externes.</li>
    <li>Profitez de l'<strong class="font-semibold">écosystème LangChain</strong> pour étendre facilement : reranking, mémoire, agents, évaluation.</li>
  </ul>

  <p class="mb-4 text-gray-700 dark:text-gray-300">Contactez-nous chez <a href="https://www.mesh-box.fr/contact" class="text-blue-600 hover:underline">Mesh Box</a> pour un PoC gratuit et découvrez comment cette architecture peut transformer votre intégration IA.</p>

  <p class="text-sm mt-8 text-mesh-600 dark:text-mesh-300">Sources : Documentation officielle LangChain, Qdrant, HuggingFace, et retours d'expérience projets Mesh Box.</p>
</div>
`;
